import { registerPlugin } from '@capacitor/core';
import { isNativeShell } from '../auth/authRedirect';

const LineMedicSecureStorage = registerPlugin('LineMedicSecureStorage');
const MEMBER_BIOMETRIC_SESSION_KEY = 'line-medic-native-member-biometric-unlocked';
const ADMIN_BIOMETRIC_SESSION_KEY = 'line-medic-native-admin-biometric-unlocked';

function currentPath() {
  if (typeof window === 'undefined') return '/';
  return String(window.location?.pathname || '/').replace(/\/+$/, '') || '/';
}

function isAdminBiometricRoute() {
  const path = currentPath();
  return path === '/admin' || path.startsWith('/admin/') || path === '/inventory-admin';
}

function sessionFlag(key) {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage?.getItem(key) === '1';
  } catch (_) {
    return false;
  }
}

function setSessionFlag(key, unlocked) {
  if (typeof window === 'undefined') return;
  try {
    if (unlocked) window.sessionStorage?.setItem(key, '1');
    else window.sessionStorage?.removeItem(key);
  } catch (_) {}
}

function memberBiometricSessionUnlocked() {
  return sessionFlag(MEMBER_BIOMETRIC_SESSION_KEY);
}

function adminBiometricSessionUnlocked() {
  return isAdminBiometricRoute() && sessionFlag(ADMIN_BIOMETRIC_SESSION_KEY);
}

function setMemberBiometricSessionUnlocked(unlocked) {
  setSessionFlag(MEMBER_BIOMETRIC_SESSION_KEY, unlocked);
}

function setAdminBiometricSessionUnlocked(unlocked) {
  setSessionFlag(ADMIN_BIOMETRIC_SESSION_KEY, unlocked);
}

function normalizeBiometricStatus(result = {}) {
  const biometryType = String(result?.biometryType || 'none');
  const label = String(result?.label || (
    biometryType === 'faceID' ? 'Face ID' : biometryType === 'touchID' ? 'Touch ID' : 'Biometrics'
  ));
  return {
    success: result?.success !== false,
    available: result?.available === true,
    enabled: result?.enabled === true,
    configured: result?.configured === true,
    sessionUnlocked: result?.sessionUnlocked === true,
    biometryType,
    label,
    message: String(result?.message || ''),
  };
}

export const nativeAuthStorage = {
  async getItem(key) {
    if (!isNativeShell()) return null;
    const result = await LineMedicSecureStorage.get({ key: String(key || '') });
    return typeof result?.value === 'string' ? result.value : null;
  },

  async setItem(key, value) {
    if (!isNativeShell()) return;
    await LineMedicSecureStorage.set({
      key: String(key || ''),
      value: String(value ?? ''),
    });
  },

  async removeItem(key) {
    if (!isNativeShell()) return;
    await LineMedicSecureStorage.remove({ key: String(key || '') });
  },
};

export function nativeAuthStorageEnabled() {
  return isNativeShell();
}

export function clearNativeBiometricSession() {
  setMemberBiometricSessionUnlocked(false);
  setAdminBiometricSessionUnlocked(false);
}

export async function getNativeBiometricStatus() {
  if (!isNativeShell()) return normalizeBiometricStatus({ available: false, configured: true });

  const adminRoute = isAdminBiometricRoute();

  // The elevated Program Admin boundary is valid only while the user remains in Program Admin.
  if (!adminRoute) setAdminBiometricSessionUnlocked(false);

  try {
    const status = normalizeBiometricStatus(await LineMedicSecureStorage.biometricStatus());

    // Settings reports the persisted native preference. It must not flip to Off merely
    // because this WebView session has already passed Face ID.
    if (currentPath() === '/settings') {
      return { ...status, sessionUnlocked: status.enabled && memberBiometricSessionUnlocked() };
    }

    // Program Admin is a separate elevated boundary. A normal member-session unlock
    // must never satisfy it; only the admin-session marker may suppress the prompt.
    if (adminRoute) {
      if (adminBiometricSessionUnlocked()) return { ...status, enabled: false, sessionUnlocked: true };
      return { ...status, sessionUnlocked: false };
    }

    // Normal member pages keep the established no-repeat-prompt contract.
    if (memberBiometricSessionUnlocked()) return { ...status, enabled: false, sessionUnlocked: true };
    return status;
  } catch (error) {
    return normalizeBiometricStatus({
      success: false,
      available: false,
      configured: true,
      message: error?.message || 'Biometric unlock is unavailable.',
    });
  }
}

export async function setNativeBiometricEnabled(enabled) {
  if (!isNativeShell()) return normalizeBiometricStatus({ available: false, configured: true });
  if (enabled !== true) clearNativeBiometricSession();
  try {
    const result = normalizeBiometricStatus(await LineMedicSecureStorage.setBiometricEnabled({
      enabled: enabled === true,
      reason: enabled ? 'Enable biometric unlock for Line Medic' : undefined,
    }));
    if (enabled === true && result.success && result.enabled) {
      setMemberBiometricSessionUnlocked(true);
      setAdminBiometricSessionUnlocked(false);
      return { ...result, sessionUnlocked: true };
    }
    return { ...result, sessionUnlocked: false };
  } catch (error) {
    return normalizeBiometricStatus({
      success: false,
      available: false,
      configured: true,
      message: error?.message || 'Unable to change biometric unlock.',
    });
  }
}

export async function authenticateNativeBiometric(reason = 'Unlock Line Medic') {
  if (!isNativeShell()) return normalizeBiometricStatus({ success: false, available: false, configured: true });
  try {
    const result = normalizeBiometricStatus(await LineMedicSecureStorage.authenticateBiometric({ reason }));
    if (result.success) {
      if (isAdminBiometricRoute()) {
        setAdminBiometricSessionUnlocked(true);
      } else {
        setMemberBiometricSessionUnlocked(true);
        setAdminBiometricSessionUnlocked(false);
      }
      return { ...result, sessionUnlocked: true };
    }
    return { ...result, sessionUnlocked: false };
  } catch (error) {
    return normalizeBiometricStatus({
      success: false,
      available: false,
      configured: true,
      message: error?.message || 'Biometric authentication failed.',
    });
  }
}
